// Agg-context functions — `sum`, `mean`, `min`, `max`, `count`, `first`,
// `last`, `value`, `values`, `timestamps`. Reachable ONLY inside
// `(aggregate …)` positional pairs and `(time-bucket :agg …)` — they
// are registered in `aggMeta`, NOT `opMeta`, so calling at top level
// surfaces `UnknownOpError` via the standard scope-restriction
// mechanism.
//
// Each func declares a `compileAggExpr` callback that translates the
// raw op call into an `AggSpec` (engine-side IR). The parent op's
// apply (`aggregate`, `time-bucket`) collects the specs into a rollup
// map and hands off to `runtime/arquero.ts::compileAggSpec` for the
// arquero translation — preserves the arquero boundary (only
// `runtime/arquero.ts` knows about arquero).
//
// Col-ref args are keyword-only: `(sum :revenue)`, `(mean :price)`,
// `(count :col)`. Per-row expressions are not accepted — they live
// upstream in `(derive ds :flagged (if cond 1 0))` followed by
// `(sum :flagged)`. This structurally prevents cross-dataset
// aggregate bugs: the keyword resolves against the parent aggregate's
// row, period.

import { linearRegression } from 'simple-statistics';
import { z } from 'zod';

import { trapezoidIntegral } from '../../../stats/series-window.ts';
import { theilSenSlope } from '../../../timeseries/insights/trend.ts';
import { MS_PER_DAY } from '../../../utils/date.ts';
import type { SourcePos } from '../../ast.ts';
import { parseOpCall, TypeMismatchError, throwErrors, UnknownOpError, zodIssuesToErrors } from '../../errors.ts';
import { opCallPos, type Spans } from '../../normalize.ts';
import {
  type AggOpMeta,
  type AggSpec,
  type ArqueroAggOpName,
  aggMeta,
  createOpTable,
  defineScope,
  type NameTable,
  type Scope,
} from '../../op-meta.ts';
import { paramPath } from '../../path.ts';
import { type AggReducer, compileAggSpec } from '../../runtime/arquero.ts';
import { type ColumnNameAst, ColumnNameAstSchema, columnNameAstString } from '../../schema/keyword.ts';
import {
  type ColumnType,
  isNumericColumn,
  numberCol,
  replaceUnit,
  type Schema,
  schemaFromEntries,
  unknownCol,
  unwrapNullable,
} from '../../schema/types.ts';
import { cellNumber } from '../../value.ts';
import { toDateValue } from './date-utils.ts';
import { SCAN_OP_NAMES } from './window.ts';

/**
 * SSOT: extract the required column-name arg from an agg call's first
 * positional. Used by scalar reducers (`sum`/`mean`/…) where the
 * keyword col-ref is mandatory.
 */
const requiredColArg = (call: { readonly params?: readonly unknown[] }): string =>
  columnNameAstString((call.params ?? [])[0] as ColumnNameAst);

/**
 * SSOT: extract an optional column-name arg from an agg call's first
 * positional, falling back to `defaultCol` when absent. Used by the
 * polymorphic TS-ref aggs (`value`/`values`) where the inner column
 * defaults to `value` when no keyword is supplied.
 */
const optionalColArg = (call: { readonly params?: readonly unknown[] }, defaultCol: string): string => {
  const arg = (call.params ?? [])[0] as ColumnNameAst | undefined;
  return arg === undefined ? defaultCol : columnNameAstString(arg);
};

/**
 * Factory: build a standard scalar-reducer op (sum, mean, min, max,
 * first, last). Takes a single keyword column ref: `(sum :revenue)`.
 * The keyword resolves against the parent aggregate's row, producing
 * an `(r) => r[col]` row-function that feeds the standard
 * `VALUE_KERNELS` reducer for `name`.
 */
const makeColAggOp = <NameType extends ArqueroAggOpName>(
  name: NameType,
  result: (input: ColumnType | undefined) => ColumnType,
) => {
  return z
    .object({
      name: z.literal(name),
      params: z.tuple([ColumnNameAstSchema]),
    })
    .describe(`Aggregate the named column with ${name}.`)
    .register(aggMeta, {
      effect: 'pure',
      example: { expr: `(${name} :revenue)` },
      terseGroup: 'agg-basic',
      compileAggExpr: async (call): Promise<AggSpec> => {
        const col = requiredColArg(call);
        return { kind: 'expr-agg', op: name, rowFn: (row) => row[col] };
      },
      resultType: result,
    } satisfies AggOpMeta);
};

/**
 * Result-type policy for order-statistic / positional aggs (`min`,
 * `max`, `first`, `last`): the output is one of the input column's own
 * values, so it carries the input KIND (nullable stripped — a group is
 * non-empty, so e.g. `max` of a `timestamp` column is a `timestamp`,
 * not `timestamp?`). Unknown input ⇒ unknown output. The declared unit
 * hint is stripped: units are value-level — the kernels move/unify the
 * per-row boxes, and any column-level unit comes from `refineSchema`.
 */
const preserveInputType = (input: ColumnType | undefined): ColumnType =>
  input === undefined ? unknownCol : replaceUnit(unwrapNullable(input), undefined);

/**
 * Result-type policy for arithmetic aggs (`sum`, `mean`, …): plain numeric.
 * Units are value-level — the aggregation kernels box a group-unified unit
 * onto the result cells; the type level never threads one.
 */
const numericResult = (): ColumnType => numberCol;

export const sumOp = makeColAggOp('sum', numericResult);
export const meanOp = makeColAggOp('mean', numericResult);
export const minOp = makeColAggOp('min', preserveInputType);
export const maxOp = makeColAggOp('max', preserveInputType);
export const firstOp = makeColAggOp('first', preserveInputType);
export const lastOp = makeColAggOp('last', preserveInputType);
export const stddevOp = makeColAggOp('stddev', numericResult);

export const varianceOp = makeColAggOp('variance', numericResult);

export const medianOp = makeColAggOp('median', numericResult);

export const rangeOp = makeColAggOp('range', numericResult);

/**
 * `(percentile :col p)` — the p-th percentile (0–100) of a numeric column.
 * `(percentile :rate 90)` is P90; `(percentile :rate 50)` is the median. `p` is
 * a positional number (agg funcs are positional-only) carried into the
 * `quantile-agg` spec as a 0–1 fraction.
 */
export const percentileOp = z
  .object({
    name: z.literal('percentile'),
    params: z.tuple([ColumnNameAstSchema, z.coerce.number().min(0).max(100)]),
  })
  .describe('The p-th percentile (0–100) of a numeric column — `(percentile :rate 90)` for P90, `50` for the median.')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(percentile :rate 90)' },
    compileAggExpr: async (call): Promise<AggSpec> => {
      const col = requiredColArg(call);
      const pct = (call.params ?? [])[1] as number;
      return { kind: 'quantile-agg', rowFn: (row) => row[col], p: pct / 100 };
    },
    resultType: numericResult,
  } satisfies AggOpMeta);
/**
 * Extract ascending (timestamp-ms, value) numeric pairs from a per-cell
 * timeseries Dataset's inner rows — the shared input for the time-aware aggs
 * below. Drops rows with an unparseable `timestamp` or non-finite `value`.
 */
const tsPairs = (rows: readonly Record<string, unknown>[]): { readonly ts: number[]; readonly vs: number[] } => {
  const pairs: { readonly t: number; readonly v: number }[] = [];
  for (const row of rows) {
    const t = toDateValue(row.timestamp);
    const v = cellNumber(row.value);
    if (t !== null && Number.isFinite(v)) {
      pairs.push({ t: t.getTime(), v });
    }
  }
  const sorted = [...pairs].sort((a, b) => a.t - b.t);
  return { ts: sorted.map((p) => p.t), vs: sorted.map((p) => p.v) };
};

/**
 * Time-weighted average: the trapezoidal area under the series divided by its
 * total time span (∫v·dt / ∫dt). `ts` is epoch-ms ascending, `vs` the aligned
 * values, both equal length and already cleaned of gaps — weighting by elapsed
 * time (Δt) rather than sample count, the correct treatment for irregularly-
 * sampled production/sensor data. Falls back to the arithmetic value for a
 * single sample or a zero-length span. `null` for an empty series.
 */
const timeWeightedMean = (ts: readonly number[], vs: readonly number[]): number | null => {
  if (vs.length === 0) {
    return null;
  }
  if (vs.length === 1) {
    return vs[0] ?? null;
  }
  const { weighted, span } = ts.slice(1).reduce(
    (acc, t, j) => {
      const dt = t - (ts[j] as number);
      return {
        weighted: acc.weighted + 0.5 * ((vs[j + 1] as number) + (vs[j] as number)) * dt,
        span: acc.span + dt,
      };
    },
    { weighted: 0, span: 0 },
  );
  return span === 0 ? (vs.at(-1) ?? null) : weighted / span;
};

/**
 * Fraction of total time the series spends strictly above `threshold` — i.e.
 * uptime / availability. Each sample's value is held over the interval that
 * follows it (left-step), the standard convention for status-over-time.
 * Returns 0..1, or `null` for an empty series.
 */
const timeWeightedFractionAbove = (ts: readonly number[], vs: readonly number[], threshold: number): number | null => {
  if (vs.length === 0) {
    return null;
  }
  if (ts.length === 1) {
    return (vs[0] as number) > threshold ? 1 : 0;
  }
  const { above, span } = ts.slice(1).reduce(
    (acc, t, j) => {
      const dt = t - (ts[j] as number);
      return {
        above: (vs[j] as number) > threshold ? acc.above + dt : acc.above,
        span: acc.span + dt,
      };
    },
    { above: 0, span: 0 },
  );
  if (span === 0) {
    return (vs[0] as number) > threshold ? 1 : 0;
  }
  return above / span;
};

/**
 * `(twa)` — time-weighted average of a per-cell timeseries (TS-ref column):
 * ∫value·dt / ∫dt over inner `timestamp`/`value`. The unbiased "average rate"
 * for irregularly-sampled data, unlike the plain sample `(mean)`.
 */
export const twaOp = z
  .object({ name: z.literal('twa'), params: z.tuple([]) })
  .describe('Time-weighted average of a per-cell timeseries (∫v·dt / ∫dt) — the sampling-robust mean.')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(twa)' },
    compileAggExpr: async (): Promise<AggSpec> => ({
      kind: 'reduce-rows',
      reduce: (rows) => {
        const { ts, vs } = tsPairs(rows);
        return timeWeightedMean(ts, vs);
      },
    }),
    resultType: (): ColumnType => numberCol,
  } satisfies AggOpMeta);
/**
 * `(integral)` — total time integral (∫rate·dt) of a per-cell production-rate
 * timeseries over its inner `timestamp`/`value`, in the rate's per-day base
 * volume (e.g. STB from STB/d). The produced-volume sibling of `twa` (the
 * time-weighted average rate) and of `eur` (which adds Arps-projected future
 * reserves). `null` for fewer than two finite samples.
 */
export const integralOp = z
  .object({ name: z.literal('integral'), params: z.tuple([]) })
  .describe('Total time integral (∫rate·dt) of a per-cell production-rate timeseries → produced volume.')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(integral)' },
    compileAggExpr: async (): Promise<AggSpec> => ({
      kind: 'reduce-rows',
      reduce: (rows) => {
        const { ts, vs } = tsPairs(rows);
        if (vs.length < 2) {
          return null;
        }
        const running = trapezoidIntegral(ts, vs, MS_PER_DAY);
        return running.at(-1) ?? null;
      },
    }),
    resultType: (): ColumnType => numberCol,
  } satisfies AggOpMeta);

/**
 * `(uptime threshold)` — fraction of time (0–1) a per-cell timeseries stays
 * strictly above `threshold`, weighted by elapsed time. Production
 * availability: e.g. `(uptime 0)` is the fraction of time a well is flowing.
 */
export const uptimeOp = z
  .object({ name: z.literal('uptime'), params: z.tuple([z.coerce.number()]) })
  .describe(
    'Time-weighted fraction (0–1) a per-cell timeseries spends above a threshold — `(uptime 0)` for flowing time.',
  )
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(uptime 0)' },
    compileAggExpr: async (call): Promise<AggSpec> => {
      const threshold = (call.params ?? [])[0] as number;
      return {
        kind: 'reduce-rows',
        reduce: (rows) => {
          const { ts, vs } = tsPairs(rows);
          return timeWeightedFractionAbove(ts, vs, threshold);
        },
      };
    },
    resultType: (): ColumnType => numberCol,
  } satisfies AggOpMeta);

/**
 * `(slope)` — robust (Theil–Sen) trend slope of a per-cell timeseries, in
 * value-units per day. Positive ⇒ rising, negative ⇒ declining.
 */
export const slopeOp = z
  .object({ name: z.literal('slope'), params: z.tuple([]) })
  .describe('Robust (Theil–Sen) trend slope of a per-cell timeseries, in value-units per day.')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(slope)' },
    compileAggExpr: async (): Promise<AggSpec> => ({
      kind: 'reduce-rows',
      reduce: (rows) => {
        const { ts, vs } = tsPairs(rows);
        return vs.length < 2 ? null : theilSenSlope(ts.map((timestamp, i) => ({ timestamp, value: vs[i] as number })));
      },
    }),
    resultType: (): ColumnType => numberCol,
  } satisfies AggOpMeta);

const DAYS_PER_YEAR = 365.25;

/**
 * Decline floor (per day) below which a rate is treated as non-declining — EUR
 * via `q/D` diverges as `D → 0`, so a flat/near-flat series (whose OLS slope is
 * just floating-point noise) has no finite ultimate recovery. ~3.7e-7 %/yr;
 * far below any real decline (a slow 0.1 %/yr is ~2.7e-6/day).
 */
const MIN_DECLINE_PER_DAY = 1e-9;

/** A fitted exponential decline: rate `qi` at the first sample, nominal decline `D` per day. */
type ExponentialDecline = { readonly qi: number; readonly declinePerDay: number };

/**
 * Fit q(t) = qi·e^(−D·t) by OLS of ln(q) on elapsed days. Only strictly
 * positive rates contribute (ln is undefined otherwise); needs at least two
 * such samples, else `null`.
 */
const fitExponentialDecline = (tsMs: readonly number[], qs: readonly number[]): ExponentialDecline | null => {
  if (tsMs.length === 0) {
    return null;
  }
  const t0 = tsMs[0] as number;
  const pts = qs.reduce<[number, number][]>((acc, q, i) => {
    if (q !== undefined && q > 0 && Number.isFinite(q)) {
      acc.push([((tsMs[i] as number) - t0) / MS_PER_DAY, Math.log(q)]);
    }
    return acc;
  }, []);
  if (pts.length < 2) {
    return null;
  }
  const { m, b } = linearRegression(pts);
  return { qi: Math.exp(b), declinePerDay: -m };
};

/**
 * Nominal annual decline as a fraction per year (e.g. `0.15` = 15 %/yr).
 * `null` when no exponential decline can be fit. Negative values indicate the
 * rate is increasing over the fitted window.
 */
const nominalAnnualDecline = (tsMs: readonly number[], qs: readonly number[]): number | null => {
  const fit = fitExponentialDecline(tsMs, qs);
  return fit === null ? null : fit.declinePerDay * DAYS_PER_YEAR;
};

/**
 * Estimated Ultimate Recovery (volumetric): cumulative produced over the
 * observed window (trapezoidal ∫rate·dt) plus remaining reserves projected by
 * the fitted exponential decline down to `abandonmentRate`. Rates are per-day;
 * EUR comes out in the rate's base volume unit. `null` unless a positive
 * decline can be fit (a flat/rising rate has no finite ultimate recovery).
 */
const estimatedUltimateRecovery = (
  tsMs: readonly number[],
  qs: readonly number[],
  abandonmentRate: number,
): number | null => {
  const fit = fitExponentialDecline(tsMs, qs);
  if (fit === null || fit.declinePerDay < MIN_DECLINE_PER_DAY) {
    return null;
  }
  const running = trapezoidIntegral(tsMs, qs, MS_PER_DAY);
  const cumObserved = [...running].reverse().find((v): v is number => v !== null) ?? 0;
  const tLastDays = ((tsMs.at(-1) ?? 0) - (tsMs[0] as number)) / MS_PER_DAY;
  const qLast = fit.qi * Math.exp(-fit.declinePerDay * tLastDays);
  const remaining = qLast > abandonmentRate ? (qLast - abandonmentRate) / fit.declinePerDay : 0;
  return cumObserved + remaining;
};

/**
 * `(decline-rate)` — nominal annual decline (%/yr) of a per-cell production-
 * rate timeseries, from an Arps exponential fit. Positive ⇒ declining
 * production; null if no decline can be fit (e.g. fewer than two positive
 * samples).
 */
export const declineRateOp = z
  .object({ name: z.literal('decline-rate'), params: z.tuple([]) })
  .describe('Nominal annual decline (%/yr) of a per-cell production-rate timeseries (Arps exponential fit).')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(decline-rate)' },
    compileAggExpr: async (): Promise<AggSpec> => ({
      kind: 'reduce-rows',
      reduce: (rows) => {
        const { ts, vs } = tsPairs(rows);
        const decline = nominalAnnualDecline(ts, vs);
        return decline === null ? null : decline * 100;
      },
    }),
    resultType: (): ColumnType => numberCol,
  } satisfies AggOpMeta);

/**
 * `(eur abandonment-rate)` — Estimated Ultimate Recovery (volumetric) of a
 * per-cell production-rate timeseries: cumulative produced so far plus
 * reserves projected by the Arps exponential decline down to
 * `abandonment-rate` (same per-day rate units). Null if no decline can be fit.
 */
export const eurOp = z
  .object({ name: z.literal('eur'), params: z.tuple([z.coerce.number().nonnegative()]) })
  .describe('Estimated Ultimate Recovery of a per-cell production-rate timeseries, down to an abandonment rate.')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(eur 10)' },
    compileAggExpr: async (call): Promise<AggSpec> => {
      const abandonmentRate = (call.params ?? [])[0] as number;
      return {
        kind: 'reduce-rows',
        reduce: (rows) => {
          const { ts, vs } = tsPairs(rows);
          return estimatedUltimateRecovery(ts, vs, abandonmentRate);
        },
      };
    },
    resultType: (): ColumnType => numberCol,
  } satisfies AggOpMeta);

/**
 * `(count)` / `(count :col)` — polymorphic by arity. Zero-arg counts
 * every row in the current group; one-keyword-arg counts non-null
 * values of the named column. Useful in
 * `(aggregate ds :group-by [:region] :pairs {:n (count)})` for group
 * sizes and `{:nz (count :revenue)}` for non-null per-column counts.
 */
export const countOp = z
  .object({
    name: z.literal('count'),
    params: z.union([z.tuple([]), z.tuple([ColumnNameAstSchema])]),
  })
  .describe('Count rows in the current group, or non-null values of a column when a keyword is supplied.')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(count)' },
    compileAggExpr: async (call): Promise<AggSpec> => {
      if ((call.params ?? []).length === 0) {
        return { kind: 'row-count' };
      }
      const col = requiredColArg(call);
      return { kind: 'expr-agg', op: 'count', rowFn: (row) => row[col] };
    },
    resultType: (): ColumnType => numberCol,
  } satisfies AggOpMeta);

/**
 * `(value)` / `(value :col)` — TS-ref shorthand for the latest value
 * of a per-cell timeseries Dataset's named inner column (defaults to
 * `value`). Only meaningful when the agg-context runs inside a
 * per-cell lifting (see `evalVerbCall`'s nested-dataset branch) over
 * a TS-ref column: the inner schema is `{ timestamp, value, isString,
 * unit }` and `(value)` picks `last(value)` (timestamps are sorted
 * ascending by convention). Pass an explicit inner-column keyword
 * (`(value :temperature)`) when the per-cell Dataset carries a
 * non-default payload column.
 *
 * Outside that per-cell context the inner column doesn't exist —
 * `runtime/arquero.ts` will fail at rollup compilation if the source
 * dataset has no such column. This is the LLM-facing pattern: pair
 * `(value)` with TS-ref columns exclusively.
 */
export const valueOp = z
  .object({
    name: z.literal('value'),
    params: z.union([z.tuple([]), z.tuple([ColumnNameAstSchema])]),
  })
  .describe('Latest value of a per-cell timeseries Dataset (TS-ref column); defaults to inner `:value`.')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(value)' },
    compileAggExpr: async (call): Promise<AggSpec> => ({
      kind: 'col-pick',
      col: optionalColArg(call, 'value'),
      pick: 'last',
    }),
    // `(value)` picks one of the inner `value` column's own cells (the
    // latest), so it carries that column's type — string for a string
    // series, timestamp for an epoch-ms series, numeric otherwise — never
    // a hardcoded numeric that would coerce a string/date cell to `NaN`.
    resultType: preserveInputType,
  } satisfies AggOpMeta);

/**
 * `(values)` / `(values :col)` — collect a per-cell TS-ref Dataset's
 * named inner column (defaults to `value`) into an array. Same
 * per-cell precondition as `(value)`.
 */
export const valuesOp = z
  .object({
    name: z.literal('values'),
    params: z.union([z.tuple([]), z.tuple([ColumnNameAstSchema])]),
  })
  .describe('Collect the full inner column array of a per-cell timeseries Dataset; defaults to `:value`.')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(values)' },
    compileAggExpr: async (call): Promise<AggSpec> => ({
      kind: 'col-array',
      col: optionalColArg(call, 'value'),
    }),
  } satisfies AggOpMeta);

/**
 * `(timestamps)` — sibling of `(values)`: collect the `timestamp` column
 * of a per-cell TS-ref Dataset into an array.
 */
export const timestampsOp = z
  .object({
    name: z.literal('timestamps'),
    params: z.tuple([]),
  })
  .describe('Collect the full `timestamp` array of a per-cell timeseries Dataset (TS-ref column).')
  .register(aggMeta, {
    effect: 'pure',
    example: { expr: '(timestamps)' },
    compileAggExpr: async (): Promise<AggSpec> => ({ kind: 'col-array', col: 'timestamp' }),
  } satisfies AggOpMeta);

/** All agg-context func schemas, ordered for stable doc rendering. */
export const aggContextFuncs = [
  sumOp,
  meanOp,
  minOp,
  maxOp,
  countOp,
  firstOp,
  lastOp,
  stddevOp,
  varianceOp,
  medianOp,
  rangeOp,
  percentileOp,
  valueOp,
  valuesOp,
  timestampsOp,
  twaOp,
  integralOp,
  uptimeOp,
  slopeOp,
  declineRateOp,
  eurOp,
] as const;
/** Union of every agg-context func schema — generic agg-schema type. */
export type AggFuncSchema = (typeof aggContextFuncs)[number];

/**
 * Agg-context scope — packaged registry + schemas list for the doc
 * walker and for runtime dispatch tables. Parent verbs (`aggregate`,
 * `time-bucket`) declare `childScopes: [aggContextScope]` so their
 * children resolve from this scope's registry. Adding a new agg
 * func is one `aggMeta.register` + one append to `aggContextFuncs`
 * — no edits to evaluator or doc generator.
 */
export const aggContextScope: Scope<AggOpMeta> = defineScope({
  name: 'Aggregation functions',
  description:
    'Reachable inside `(aggregate …)` positional pairs and `(time-bucket :agg …)`. Each func collapses a group of rows (or a per-cell timeseries Dataset) into a single value.',
  registry: aggMeta,
  schemas: aggContextFuncs,
});
/**
 * Map-literal SSOT for `{:out (agg-call …)}` slots — the parser-produced
 * `{$map: [{k, v}]}` shape with `k` an out-column-name and `v` an
 * agg-call op-call shape (e.g. `(sum :revenue)`, `(count)`). Consumed
 * by `aggregate :pairs` and `time-bucket :agg` (plain form). At least
 * one entry is required (an empty `{}` is a user error); `slotLabel`
 * is interpolated into the empty-map diagnostic so the error blames
 * the right kwarg.
 */
const buildAggPairsMapSchema = (slotLabel: string) =>
  z
    .object({
      $map: z
        .array(
          z.object({
            k: z.string().min(1),
            v: z.object({
              name: z.string().min(1),
              params: z.array(z.unknown()).optional(),
            }),
          }),
        )
        .min(1, { error: `\`${slotLabel}\` must declare at least one \`{:out-name (agg-call …)}\` entry` }),
    })
    .strict();

// Building this Zod schema tree (ZodObject → ZodArray → ZodObject …) is far
// costlier than parsing a small map against it, and the shape is identical
// per `slotLabel` — only the empty-map error message varies. The plain-form
// `time-bucket :agg` re-validates through `parsePlainAggMap` once per call
// (~1500× in profiled traces), so without memoization every call rebuilds
// the whole tree and discards it. There are only ever two labels (`:pairs`,
// `:agg`), so cache by label: construction drops to two trees process-wide.
const aggPairsMapSchemaCache = new Map<string, ReturnType<typeof buildAggPairsMapSchema>>();

export const aggPairsMapSchema = (slotLabel: string): ReturnType<typeof buildAggPairsMapSchema> => {
  const cached = aggPairsMapSchemaCache.get(slotLabel);
  if (cached !== undefined) {
    return cached;
  }
  const schema = buildAggPairsMapSchema(slotLabel);
  aggPairsMapSchemaCache.set(slotLabel, schema);
  return schema;
};

/**
 * The type of the map data the schema produces (post-parse). Both
 * consumers (aggregate / time-bucket plain) iterate over `.data.$map`.
 */
export type AggPairsMapEntry = {
  readonly k: string;
  readonly v: { readonly name: string; readonly params?: readonly unknown[] };
};

/** Module-level agg-func name table — built once at module load and
 * shared by every verb that dispatches into the agg-context. */
export const AGG_FUNCS_TABLE: NameTable = createOpTable(aggContextScope.schemas as readonly z.ZodObject[]);

/** Compiled agg pair — the (out-name, manual reducer) record consumed
 * by `aggregate` / `time-bucket` apply when iterating rolled-up rows. */
export type CompiledAggPair = {
  readonly outName: string;
  readonly reduce: (rows: readonly Record<string, unknown>[]) => unknown;
};

/**
 * Self-correcting hint for a window/scan op named where an agg reducer is
 * expected. These ops produce a column, so they belong in the pipeline (with
 * `:as`), then the running result is collapsed by a real reducer.
 */
const scanOpInAggHint = (name: string): string =>
  `\`${name}\` is a window op that produces a column, not an aggregate reducer — apply it as a pipeline stage with \`:as\` (e.g. \`(-> ds (${name} :col :as :out))\`), then reduce the result with \`(last :out)\` or \`(first :out)\`.`;

/**
 * Compile a single agg-call opcall (`(sum :revenue)`, `(count)`, …)
 * into an `AggReducer`. Routes through `AGG_FUNCS_TABLE` for the
 * agg-func schema lookup, then `aggMeta` for the func's
 * `compileAggExpr` → `AggSpec` → `AggReducer`. The agg-call is
 * hard-validated against its registered schema via `parseOpCall`
 * (engine SSOT for op-call shape validation) — malformed inputs
 * (wrong arity, wrong arg structure) raise a loud `ShapeError` with
 * the supplied path's sourcePos, NEVER silently coerce to a null/zero
 * aggregate.
 *
 * SSOT for "translate an (agg-call) opcall into a per-group reducer"
 * — consumed by `compileAggPair` (aggregate / time-bucket pair form)
 * and by `pivot`'s `:values` slot. Adding a new agg-context verb is
 * one registration.
 */
export const compileAggCallReducer = async (
  aggCall: { readonly name: string; readonly params?: readonly unknown[] },
  path: string,
  spans: Spans,
): Promise<AggReducer> => {
  const schema = AGG_FUNCS_TABLE.get(aggCall.name);
  if (schema === undefined) {
    throw new UnknownOpError(
      aggCall.name,
      Array.from(AGG_FUNCS_TABLE.keys()),
      opCallPos(spans, path),
      SCAN_OP_NAMES.has(aggCall.name) ? scanOpInAggHint(aggCall.name) : undefined,
    );
  }
  const meta = aggMeta.get(schema);
  if (meta === undefined) {
    throw new Error(`compileAggCallReducer: schema for \`${aggCall.name}\` not registered in aggMeta`);
  }
  const parsed = parseOpCall(schema, { name: aggCall.name, params: aggCall.params ?? [] });
  if (!parsed.success) {
    throwErrors(zodIssuesToErrors(parsed.error, path, spans));
  }
  // `meta.compileAggExpr`'s stored return is `unknown` (Zod `$replace`
  // ceremony — see `AggOpMeta.compileAggExpr` in `op-meta.ts`); the
  // author annotates the arrow's return as `Promise<AggSpec>`, which
  // we recover by cast.
  const spec = (await meta.compileAggExpr({ name: aggCall.name, params: aggCall.params ?? [] }, path)) as AggSpec;
  return compileAggSpec(spec);
};

/**
 * Compile a single `(out-name (agg-call …))` pair into a
 * `CompiledAggPair` — `(out-name, reducer)`. Thin wrapper over
 * {@link compileAggCallReducer} that unwraps the pair's inner
 * agg-call and tags the reducer with the pair's out-name. Consumed by
 * `aggregate` and `time-bucket`.
 */
export const compileAggPair = async (
  pair: {
    readonly name: string;
    readonly params: readonly { readonly name: string; readonly params?: readonly unknown[] }[];
  },
  basePath: string,
  spans: Spans,
): Promise<CompiledAggPair> => {
  const aggCall = pair.params[0];
  // `pair.params[0]` is the inner agg-call; its path is `basePath/0/0`
  // (pair's params vector, index 0, agg-call's own params root). The
  // existing convention is kept so error messages don't shift.
  const path = paramPath(paramPath(basePath, 0), 0);
  if (aggCall === undefined) {
    throw new UnknownOpError('', Array.from(AGG_FUNCS_TABLE.keys()), opCallPos(spans, path));
  }
  return { outName: pair.name, reduce: await compileAggCallReducer(aggCall, path, spans) };
};

/** A keyword AST node for the TS-ref inner `value` column. */
const VALUE_COL_KW = { $kw: 'value' } as const;

/**
 * Synthesise the params array for the bare per-cell `:agg` form of
 * `<symbol>`, or `null` if no synthesis is valid for this op's schema.
 *
 * The bare form is shorthand for `(<liftCol>-<sym> (<sym> :value))`.
 * For zero-arg ops (`count` at present), it degrades to
 * `(<liftCol>-<sym> (<sym>))`.
 *
 * SSOT: bare-form eligibility is derived BEHAVIOURALLY by attempting
 * `safeParse` against the registered schema — no `bareEligible` flag,
 * no spec-kind inspection. Add a new scalar agg via `makeColAggOp`
 * and it becomes bare-eligible automatically; `timestamps` stays
 * non-eligible because its zero-arg-only schema rejects the
 * `[VALUE_COL_KW]` synthesis AND it isn't `count` (the lone zero-arg
 * bare convenience).
 */
const bareAggArgs = (symbol: string, schema: z.ZodType): readonly unknown[] | null => {
  if (parseOpCall(schema, { name: symbol, params: [VALUE_COL_KW] }).success) {
    return [VALUE_COL_KW];
  }
  if (symbol === 'count' && parseOpCall(schema, { name: symbol, params: [] }).success) {
    return [];
  }
  return null;
};

/**
 * Bare agg symbols valid in `time-bucket`'s per-cell `:agg` vector
 * form. Derived STRUCTURALLY: an agg is bare-eligible iff
 * `bareAggArgs` finds a valid synthesis for it. No meta flag, no
 * spec-kind inspection — eligibility falls out of the op's own
 * registered schema. Adding a new scalar agg via `makeColAggOp` makes
 * it bare-usable by construction; `timestamps` stays excluded because
 * its zero-arg-only schema rejects the column-arg synthesis.
 */
export const BARE_AGG_SYMBOLS: readonly string[] = Array.from(AGG_FUNCS_TABLE.entries()).flatMap(([name, schema]) =>
  bareAggArgs(name, schema) === null ? [] : [name],
);
export type BareAggSymbol = string;

/** Is `s` one of the bare per-cell `:agg` symbols? */
export const isBareAggSymbol = (s: string): s is BareAggSymbol => BARE_AGG_SYMBOLS.includes(s);

/**
 * Compile a bare agg symbol (per-cell `:agg` form) into a
 * `CompiledAggPair` over the TS-ref inner `value` column, output-named
 * `<liftColumn>-<symbol>`. Delegates to `compileAggPair` — the
 * SSOT pair compiler — by synthesising the equivalent
 * `(<liftColumn>-<symbol> (<symbol> :value))` pair, so the bare form
 * and the `(out (agg :col))` pair form share ONE aggregation source
 * (registry lookup + `compileAggExpr` + `compileAggSpec`).
 */
export const compileBareAgg = (symbol: BareAggSymbol, liftColumn: string, spans: Spans): Promise<CompiledAggPair> => {
  const schema = AGG_FUNCS_TABLE.get(symbol);
  if (schema === undefined) {
    throw new Error(`compileBareAgg: \`${symbol}\` is not in AGG_FUNCS_TABLE`);
  }
  const params = bareAggArgs(symbol, schema);
  if (params === null) {
    throw new Error(`compileBareAgg: \`${symbol}\` is not bare-eligible (no valid synthesis)`);
  }
  return compileAggPair({ name: `${liftColumn}-${symbol}`, params: [{ name: symbol, params }] }, 'p', spans);
};

/**
 * SSOT: the column an agg call aggregates, or `undefined`. Returns
 * undefined for zero-arg calls like `(count)`. For the keyword-arg
 * form (`(sum :revenue)`), returns the column name as a plain string —
 * reads the `{$kw: string}` AST shape directly (consistent with how
 * agg-call params are validated by `ColumnNameAstSchema`).
 */
export const aggInputColumn = (aggCall: { readonly params?: readonly unknown[] }): string | undefined => {
  const arg = (aggCall.params ?? [])[0];
  if (arg === undefined) {
    return undefined;
  }
  return columnNameAstString(arg as ColumnNameAst);
};

/**
 * SSOT: output `ColumnType` of an agg call, given the schema its input
 * column resolves against. Resolves the func's `AggOpMeta.resultType`
 * (→ `unknownCol` when the func declares none or the input is unknown).
 * Used by `aggregate` and per-cell `time-bucket` schema propagation so
 * a KNOWN view/source column type survives aggregation instead of
 * degrading every agg output to `unknownCol`.
 */
export const aggResultType = (
  aggCall: { readonly name: string; readonly params?: readonly unknown[] },
  inputSchema: Schema,
): ColumnType => {
  const schema = AGG_FUNCS_TABLE.get(aggCall.name);
  const meta = schema === undefined ? undefined : aggMeta.get(schema);
  if (meta?.resultType === undefined) {
    return unknownCol;
  }
  const col = aggInputColumn(aggCall);
  // `resultType` return is widened to `unknown` for the $replace-depth
  // reason documented on `AggOpMeta.resultType`; cast it back here (the
  // sanctioned dispatcher-side recovery).
  return meta.resultType(col === undefined ? undefined : inputSchema.get(col)) as ColumnType;
};
/**
 * SSOT: `(out-name, output-ColumnType)` entries for a list of
 * `(out (agg col))` pairs, each typed via {@link aggResultType}
 * against `inputSchema`. Shared by `aggregate` and plain-form
 * `time-bucket` schema propagation so the "agg pairs → typed outputs"
 * rule lives in exactly one place.
 */
export const aggOutputEntries = (
  pairs: readonly { readonly name: string; readonly params: readonly unknown[] }[],
  inputSchema: Schema,
): [string, ColumnType][] =>
  pairs.map((p) => [p.name, aggResultType(p.params[0] as { name: string; params?: readonly unknown[] }, inputSchema)]);

/**
 * Bare per-cell aggregations that require a NUMERIC value column. `sum`,
 * `mean`, `stddev`, `variance`, `median`, and `range` combine values
 * arithmetically (or by numeric order), so they are meaningless over a string
 * series; applying one to a string-valued timeseries cell is a type error.
 * Order-statistic / positional aggs (`min`/`max`/`first`/`last`) and `count`
 * are type-agnostic and absent here. SSOT for the per-cell agg value-type
 * contract.
 */
export const NUMERIC_ONLY_BARE_AGGS: ReadonlySet<string> = new Set([
  'sum',
  'mean',
  'stddev',
  'variance',
  'median',
  'range',
]);

/**
 * Guard a bare per-cell agg against its value column's type. A
 * numeric-only agg (`sum`/`mean`) over a CONCRETE non-numeric `value`
 * (e.g. a string series) is a `TypeMismatchError`. Over `unknown` the
 * check DEFERS — a top-level datapoints query declares `value:unknown`
 * (a selection may mix numeric and string series), so the concrete type
 * is only known once the series is fetched, where the materialised inner
 * schema surfaces the mismatch. Type-agnostic aggs accept any value type.
 */
export const assertBareAggValueType = (sym: string, valueType: ColumnType, pos: SourcePos): void => {
  if (!NUMERIC_ONLY_BARE_AGGS.has(sym)) {
    return;
  }
  const v = unwrapNullable(valueType);
  if (v.kind === 'unknown' || isNumericColumn(v)) {
    return;
  }
  throw new TypeMismatchError(v.kind, 'a numeric value column', `(${sym} :value)`, pos);
};

/**
 * Per-cell `time-bucket` bare-agg result type, given the lifted cell's
 * inner `value` column type. A bare per-cell agg (`mean`/`max`/…/`count`)
 * reduces the cell's `value` column; its output type follows the agg's
 * result policy resolved against the REAL value type — `sum`/`mean` →
 * `number`, `min`/`max`/`first`/`last` → the value type itself (so a
 * string series' `(last :value)` is `string`), `count` → `integer`.
 * Defaults to `number` so existing callers that omit a value type keep
 * the historical numeric-series behaviour.
 */
export const bareAggResultType = (sym: string, valueType: ColumnType = numberCol): ColumnType => {
  const schema = AGG_FUNCS_TABLE.get(sym);
  // `sym` is always a registered agg func here — `BARE_AGG_SYMBOLS` is
  // computed from `AGG_FUNCS_TABLE` so the lookup never misses for a
  // bare-eligible sym. Defensive `?? []` keeps types honest without
  // changing behaviour.
  const params = schema === undefined ? null : bareAggArgs(sym, schema);
  return aggResultType({ name: sym, params: params ?? [] }, schemaFromEntries([['value', valueType]]));
};
